import { onBeforeUnmount } from 'vue'
import cookie from 'js-cookie'
import { TOKEN } from '@/enum'
import { useEnv } from '@/hooks/useEnv'
import { Dayjs } from 'dayjs'
const isSSr = import.meta.env.SSR
const { VITE_APP_TEST_COOKIE_API } = useEnv()
export const gotoLogin = () => {
  if (isSSr) return
  location.href = `${import.meta.env.VITE_APP_LOGIN_URI}login?from=${encodeURIComponent(
    location.href,
  )}`
}

export const gotoRegister = () => {
  if (isSSr) return
  location.href = `${import.meta.env.VITE_APP_LOGIN_URI}register?from=${encodeURIComponent(
    location.href,
  )}`
}

type Fn = () => void
export const countDown = (
  interCallback: (i: number) => void | null,
  delay: number,
  startCount: number,
  finallyCallback: Fn,
) => {
  const timer = setInterval(() => {
    startCount--
    if (!startCount) {
      clear()
      finallyCallback && finallyCallback()
      return
    }
    interCallback && interCallback(startCount)
  }, delay)
  const clear = () => clearInterval(timer)
  onBeforeUnmount(clear)
}

type GlobalThis = typeof globalThis & { cookies: Record<string, string | null> }

export const token = () => {
  return cookie.get(TOKEN)
}
type cookieConfig = {
  expires?: number
  path?: string
  domain?: string
}
export const setToken = (key: string, token: string) => {
  const config: cookieConfig = { expires: 7, path: '/' }
  const obj = { ...config }
  if (VITE_APP_TEST_COOKIE_API) obj.domain = VITE_APP_TEST_COOKIE_API
  cookie.set(key, token, obj)
}

function getSubdomain() {
  try {
    let subdomain = ''
    const key = `mh_${Math.random()}`
    const expiredDate = new Date(0)
    const { domain } = document
    const domainList = domain.split('.')

    const reg = new RegExp(`(^|;)\\s*${key}=12345`)
    const ipAddressReg =
      /^(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/

    // 若为 IP 地址、localhost，则直接返回
    if (ipAddressReg.test(domain) || domain === 'localhost') {
      return domain
    }

    const urlItems = []
    urlItems.unshift(domainList.pop())

    while (domainList.length) {
      urlItems.unshift(domainList.pop())
      subdomain = urlItems.join('.')

      const cookie = `${key}=12345;domain=.${subdomain}`
      document.cookie = cookie

      if (reg.test(document.cookie)) {
        document.cookie = `${cookie};expires=${expiredDate}`
        break
      }
    }

    return subdomain || document.domain
  } catch (e) {
    return document.domain
  }
}

export const removeToken = () => {
  if (import.meta.env.SSR) {
    ;(global as GlobalThis).cookies[TOKEN] = null
  }
  const domain = getSubdomain()
  cookie.remove(TOKEN, { path: '/', domain })
}

/**
 * @description: 去抖
 * @param {function} fn 回调函数
 * @param {number} delay 延迟时间
 * @return {*}
 */
export const debounce = (fn: () => void, delay?: number) => {
  let time: ReturnType<typeof setTimeout>
  return () => {
    if (time) {
      clearTimeout(time)
    }
    time = setTimeout(() => {
      fn && fn()
    }, delay || 200)
  }
}

/**
 * @description: 节流
 * @param {function} fn 回调函数
 * @param {*} delay 延迟时间
 * @param {*} immediate	即时
 * @return {*}
 */
export const throttle = (fn: () => void, delay = 200, immediate = false) => {
  let timer: any = null
  return (...args: any) => {
    if (timer) clearInterval(timer)
    if (immediate) {
      if (!timer) fn.apply(this, args)
      timer = setTimeout(() => {
        timer = null
      }, delay)
    } else {
      timer = setTimeout(() => {
        fn.apply(this, args)
      }, delay)
    }
  }
}
/**
 * @description: 路由携带参数进行回跳
 * @param {string} url 回跳 路径
 */
export const urlJumpBack = (url: string) => {
  window.location.href = decodeURIComponent(url)
}

export const insideIframe = () => {
  return window.self !== window.top
}

/**
 * @description: 获取文件base64
 * @param {File} file 文件
 * @return {*}
 */
export function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (val) => {
      resolve(reader.result)
    }
    reader.onerror = (error) => reject(error)
  })
}

/**
 * @description: 获取图片尺寸
 * @param {File} file 文件
 * @return {*}
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      const { width, height } = img
      URL.revokeObjectURL(url) // 释放内存
      resolve({ width, height })
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('无法获取图片尺寸'))
    }

    img.src = url
  })
}
/**
 * 获取近N天的日期数组（支持按天或按小时返回）
 * @param endTime 结束时间
 * @param days 天数，默认30天
 * @param intervalType 时间间隔类型，可选 'day' 或 'hour'，默认为 'day'
 * @returns
 *   - 如果 intervalType 为 'day'：返回日期字符串数组，格式为 'YYYY-MM-DD'
 *   - 如果 intervalType 为 'hour'：返回时间戳数组，格式为 'YYYY-MM-DD HH:00:00'
 */
export const getLastNDays = (
  endTime: Dayjs | Date,
  days = 30,
  intervalType: 'day' | 'hour' = 'day',
): string[] => {
  const result: string[] = []

  // 处理 endTime 类型：统一转换为 Date 对象
  const baseDate = endTime instanceof Date ? endTime : endTime.toDate()

  // 校验日期有效性
  if (isNaN(baseDate.getTime())) {
    throw new Error('Invalid endTime: 请输入有效的 Date 对象或 Dayjs 对象')
  }

  if (intervalType === 'day') {
    // 按天返回日期数组
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(baseDate)
      date.setDate(baseDate.getDate() - i)

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0') // 月份从0开始，需要+1
      const day = String(date.getDate()).padStart(2, '0')

      result.push(`${year}-${month}-${day}`)
    }
  } else if (intervalType === 'hour') {
    // 按小时返回每天 0 点到 24 点的时间戳数组
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(baseDate)
      date.setDate(baseDate.getDate() - i)

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      // 遍历每天的 0 点到 23 点
      for (let hour = 0; hour < 24; hour++) {
        const formattedHour = String(hour).padStart(2, '0')
        result.push(`${year}-${month}-${day} ${formattedHour}:00:00`)
      }
    }
  }

  return result
}
